/*
 * @Description: IM 管理器 - 统一管理 IM 登录、状态和重试逻辑
 * @Author: Kilo Code
 * @Date: 2025-11-26
 */

/**
 * IM 管理器类
 * 负责统一管理 IM SDK 的登录、状态管理和错误处理
 */
class IMManager {
  constructor() {
    // 状态定义
    this.STATE = {
      IDLE: 'idle',           // 空闲状态
      INITIALIZING: 'initializing', // 初始化中
      LOGGING_IN: 'logging_in',   // 登录中
      LOGGED_IN: 'logged_in',     // 已登录
      ERROR: 'error',            // 错误状态
      RETRYING: 'retrying'       // 重试中
    }

    // 当前状态
    this.currentState = this.STATE.IDLE
    
    // 配置
    this.config = {
      maxRetries: 3,              // 最大重试次数
      baseRetryDelay: 2000,       // 基础重试延迟（毫秒）
      maxRetryDelay: 30000,       // 最大重试延迟（毫秒）
      retryBackoffFactor: 2,      // 退避因子
      loginTimeout: 15000         // 登录超时时间（毫秒）
    }

    // 重试相关
    this.retryCount = 0
    this.retryTimer = null
    this.loginPromise = null
    this.loginLock = false

    // 错误处理配置
    this.retryableErrors = [70001, 6206, 20002, 'NETWORK_ERROR', 'TIMEOUT']
    
    // 事件监听器
    this.eventListeners = new Map()

    // 绑定方法
    this.handleSdkReady = this.handleSdkReady.bind(this)
    this.handleNetworkChange = this.handleNetworkChange.bind(this)
  }

  /**
   * 初始化 IM 管理器
   * @param {Object} tim - TIM SDK 实例
   * @param {Object} store - Vuex store 实例
   */
  init(tim, store) {
    this.tim = tim
    this.store = store

    // 监听 SDK 事件
    this.tim.on(window.TIM.EVENT.SDK_READY, this.handleSdkReady)
    this.tim.on(window.TIM.EVENT.NET_STATE_CHANGE, this.handleNetworkChange)

    console.log('IMManager 初始化完成')
  }

  /**
   * 处理 SDK 准备就绪事件
   */
  handleSdkReady() {
    console.log('IM SDK 准备就绪')
    this.setState(this.STATE.LOGGED_IN)
    this.store.commit('TIM_SDK_READY')
    
    // SDK 准备就绪后禁用统计上报
    this.disableStatistics()
    
    // 重置重试计数
    this.retryCount = 0
    
    // 触发事件
    this.emit('sdk-ready')
  }

  /**
   * 处理网络状态变化
   * @param {Object} event - 网络状态事件
   */
  handleNetworkChange(event) {
    console.log('网络状态变化:', event)
    
    if (event.state === window.TIM.TYPES.NET_STATE_DISCONNECTED) {
      // 网络断开，准备重连
      this.setState(this.STATE.ERROR)
      this.emit('network-disconnected', event)
    } else if (event.state === window.TIM.TYPES.NET_STATE_CONNECTED) {
      // 网络恢复，尝试重新登录
      if (this.currentState === this.STATE.ERROR) {
        this.scheduleRetry()
      }
      this.emit('network-connected', event)
    }
  }

  /**
   * 尝试登录 IM
   * @param {Object} loginInfo - 登录信息 {userID, userSig}
   * @returns {Promise} 登录结果
   */
  async login(loginInfo) {
    // 检查登录锁
    if (this.loginLock) {
      console.log('IM 正在登录中，返回现有 Promise')
      return this.loginPromise
    }

    // 检查是否已经登录
    if (this.currentState === this.STATE.LOGGED_IN) {
      console.log('IM 已登录，跳过登录流程')
      return Promise.resolve({ success: true, message: '已登录' })
    }

    // 设置登录锁
    this.loginLock = true
    this.setState(this.STATE.LOGGING_IN)

    // 创建登录 Promise
    this.loginPromise = this.performLogin(loginInfo)

    try {
      const result = await this.loginPromise
      return result
    } finally {
      // 释放登录锁
      this.loginLock = false
      this.loginPromise = null
    }
  }

  /**
   * 执行实际的登录操作
   * @param {Object} loginInfo - 登录信息
   * @returns {Promise} 登录结果
   */
  async performLogin(loginInfo) {
    try {
      console.log('开始 IM 登录:', loginInfo.userID)
      
      // 设置登录超时
      const loginPromise = this.tim.login(loginInfo)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('登录超时')), this.config.loginTimeout)
      })

      const result = await Promise.race([loginPromise, timeoutPromise])
      
      console.log('IM 登录成功:', result)
      
      // 登录成功后禁用统计上报
      this.disableStatistics()
      
      return { success: true, data: result }
      
    } catch (error) {
      console.error('IM 登录失败:', error)
      
      // 分析错误并决定是否重试
      const shouldRetry = this.shouldRetry(error)
      
      if (shouldRetry && this.retryCount < this.config.maxRetries) {
        this.setState(this.STATE.RETRYING)
        this.scheduleRetry()
        return { success: false, error, willRetry: true }
      } else {
        this.setState(this.STATE.ERROR)
        this.emit('login-failed', { error, retryCount: this.retryCount })
        return { success: false, error, willRetry: false }
      }
    }
  }

  /**
   * 判断是否应该重试
   * @param {Error} error - 错误对象
   * @returns {boolean} 是否应该重试
   */
  shouldRetry(error) {
    // 检查错误码
    if (error.code && this.retryableErrors.includes(error.code)) {
      return true
    }
    
    // 检查错误消息
    if (error.message) {
      const retryableMessages = ['网络', '超时', '连接', 'timeout', 'network']
      return retryableMessages.some(msg => 
        error.message.toLowerCase().includes(msg.toLowerCase())
      )
    }
    
    return false
  }

  /**
   * 安排重试
   */
  scheduleRetry() {
    this.retryCount++
    
    // 计算延迟时间（指数退避）
    const delay = Math.min(
      this.config.baseRetryDelay * Math.pow(this.config.retryBackoffFactor, this.retryCount - 1),
      this.config.maxRetryDelay
    )
    
    console.log(`安排第 ${this.retryCount} 次重试，延迟 ${delay}ms`)
    
    this.retryTimer = setTimeout(() => {
      this.emit('retry-scheduled', { retryCount: this.retryCount, delay })
      // 这里需要重新获取登录信息并尝试登录
      this.emit('retry-login')
    }, delay)
  }

  /**
   * 禁用统计上报
   */
  disableStatistics() {
    try {
      if (this.tim && this.tim.call) {
        this.tim.call({
          name: 'disableStatistics',
          args: []
        })
        console.log('已禁用 IM 统计上报')
      }
    } catch (error) {
      console.warn('禁用 IM 统计上报失败:', error)
    }
  }

  /**
   * 登出
   */
  async logout() {
    try {
      if (this.tim) {
        await this.tim.logout()
      }
      this.setState(this.STATE.IDLE)
      this.retryCount = 0
      this.clearRetryTimer()
      console.log('IM 登出成功')
    } catch (error) {
      console.error('IM 登出失败:', error)
    }
  }

  /**
   * 设置状态
   * @param {string} newState - 新状态
   */
  setState(newState) {
    const oldState = this.currentState
    this.currentState = newState
    console.log(`IM 状态变更: ${oldState} -> ${newState}`)
    this.emit('state-change', { oldState, newState })
  }

  /**
   * 清除重试定时器
   */
  clearRetryTimer() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  /**
   * 获取当前状态
   * @returns {string} 当前状态
   */
  getState() {
    return this.currentState
  }

  /**
   * 获取重试次数
   * @returns {number} 重试次数
   */
  getRetryCount() {
    return this.retryCount
  }

  /**
   * 添加事件监听器
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event).push(callback)
  }

  /**
   * 移除事件监听器
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event)
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  /**
   * 触发事件
   * @param {string} event - 事件名称
   * @param {*} data - 事件数据
   */
  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`事件 ${event} 的回调执行失败:`, error)
        }
      })
    }
  }

  /**
   * 销毁 IM 管理器
   */
  destroy() {
    // 清除定时器
    this.clearRetryTimer()
    
    // 移除事件监听器
    if (this.tim) {
      this.tim.off(window.TIM.EVENT.SDK_READY, this.handleSdkReady)
      this.tim.off(window.TIM.EVENT.NET_STATE_CHANGE, this.handleNetworkChange)
    }
    
    // 清空事件监听器
    this.eventListeners.clear()
    
    // 重置状态
    this.setState(this.STATE.IDLE)
    this.loginLock = false
    this.loginPromise = null
    
    console.log('IMManager 已销毁')
  }
}

// 创建单例实例
const imManager = new IMManager()

export default imManager
export { IMManager }